import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksAPI, moodsAPI, recoveryAPI, companyAPI } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faArrowLeft, faCheck, faPause, faTrash, 
  faCircle, faForward, faPlay, faEye, faEyeSlash, faClock,
  faChartBar, faSpinner, faTasks, faListCheck
} from '@fortawesome/free-solid-svg-icons';
import { getUser } from '../utils/auth';
import { useReminder } from '../context/ReminderContext';
import 'react-toastify/dist/ReactToastify.css';

const TASK_WEIGHTS = [
  { value: 'light', label: 'Light', color: '#999', points: 1 },
  { value: 'medium', label: 'Medium', color: '#FFB6C1', points: 2 },
  { value: 'heavy', label: 'Heavy', color: '#FF69B4', points: 3 },
];

// Helper function to get local date in YYYY-MM-DD format (not UTC)
const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Tasks = () => {
  const navigate = useNavigate();
  const { startTaskTracking, stopTaskTracking, activeTrackers } = useReminder();
  const [activeTab, setActiveTab] = useState('my-tasks'); // 'my-tasks' | 'reports'
  const [tasks, setTasks] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);

  // Reports state
  const [myDepartments, setMyDepartments] = useState([]);
  const [selectedReportDept, setSelectedReportDept] = useState(null);
  const [deptReport, setDeptReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [reportDateTo, setReportDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [deptTasksForReport, setDeptTasksForReport] = useState([]);
  const [newTask, setNewTask] = useState({ 
    title: '', 
    description: '',
    weight: 'medium',
    scheduled_date: getLocalDateString()
  });
  const [loading, setLoading] = useState(false);
  const [capacity, setCapacity] = useState({ current: 0, max: 6 }); // FIX: Initialize current to 0
  const [moodToday, setMoodToday] = useState(null);
  const [recoveryActive, setRecoveryActive] = useState(false);
  const [showCompleted, setShowCompleted] = useState(true);
  const user = getUser();

  useEffect(() => {
    fetchTasks();
    fetchCapacityInfo();
  }, []);

  // Track active tasks for reminder duration tracking
  useEffect(() => {
    const activeTasks = tasks.filter(t => t.status === 'active');
    const currentlyTracked = activeTrackers.map(t => t.task_id);
    
    // Start tracking for new active tasks (2 hour threshold)
    activeTasks.forEach(task => {
      if (!currentlyTracked.includes(task.id)) {
        startTaskTracking(task.id, 7200); // 2 hours in seconds
      }
    });
    
    // Stop tracking for tasks that are no longer active
    activeTrackers.forEach(tracker => {
      const isStillActive = activeTasks.some(t => t.id === tracker.task_id);
      if (!isStillActive) {
        stopTaskTracking(tracker.task_id);
      }
    });
  }, [tasks, activeTrackers, startTaskTracking, stopTaskTracking]);

  // Fetch departments I belong to for the report tab
  useEffect(() => {
    if (activeTab === 'reports') {
      fetchMyDepartments();
    }
  }, [activeTab]);

  const fetchMyDepartments = async () => {
    try {
      const res = await companyAPI.getMyDepartments();
      setMyDepartments(res.data?.departments || []);
    } catch (error) {
      // silently fail — user may not be in a company
    }
  };

  useEffect(() => {
    if (selectedReportDept) {
      loadDeptReport(selectedReportDept);
    }
  }, [selectedReportDept, reportDateFrom, reportDateTo]);

  const loadDeptReport = async (deptId) => {
    setLoadingReport(true);
    try {
      const res = await companyAPI.getDepartmentReport(deptId, {
        date_from: reportDateFrom,
        date_to: reportDateTo
      });
      setDeptReport(res.data);
      // Also fetch raw tasks for client-side breakdown
      const tasksRes = await companyAPI.getDepartmentTasks(deptId);
      setDeptTasksForReport(tasksRes.data.tasks || []);
    } catch (error) {
      // Build client-side report
      try {
        const tasksRes = await companyAPI.getDepartmentTasks(deptId);
        const allT = tasksRes.data.tasks || [];
        setDeptTasksForReport(allT);
        const today = new Date(); today.setHours(0,0,0,0);
        const completed = allT.filter(t => t.status === 'completed');
        const late = allT.filter(t => t.status !== 'completed' && t.scheduled_date && new Date(t.scheduled_date) < today);
        const byAssignee = {};
        allT.forEach(t => {
          const name = t.assignee_name || 'Unknown';
          if (!byAssignee[name]) byAssignee[name] = { total: 0, completed: 0, late: 0 };
          byAssignee[name].total++;
          if (t.status === 'completed') byAssignee[name].completed++;
          if (t.status !== 'completed' && t.scheduled_date && new Date(t.scheduled_date) < today) byAssignee[name].late++;
        });
        setDeptReport({
          total: allT.length,
          completed: completed.length,
          late: late.length,
          completion_rate: allT.length ? Math.round((completed.length / allT.length) * 100) : 0,
          by_assignee: Object.entries(byAssignee).map(([name, s]) => ({ name, ...s }))
        });
      } catch {}
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchCapacityInfo = async () => {
    try {
      // Fetch mood to determine capacity
      const moodResponse = await moodsAPI.getTodayMoods();
      const moods = moodResponse.data?.moods || [];
      const clockInMood = moods.find(m => m.mood_time === 'clock-in');
      setMoodToday(clockInMood?.mood_type || null);

      // Check recovery status
      const recoveryResponse = await recoveryAPI.getStatus();
      setRecoveryActive(recoveryResponse.data?.is_active || false);

      // Calculate capacity based on mood
      const moodCapacities = {
        'motivated': 10,
        'calm': 8,
        'neutral': 6,
        'tired': 4,
        'heavy': 3
      };

      let maxCapacity = moodCapacities[clockInMood?.mood_type] || 6;
      
      // Reduce capacity in recovery mode
      if (recoveryResponse.data?.is_active) {
        maxCapacity = Math.floor(maxCapacity * 0.6);
      }

      // FIX: Use functional update to preserve current load
      setCapacity(prev => ({ ...prev, max: maxCapacity }));
    } catch (error) {
      console.error('Error fetching capacity info:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      // Use the new all-active endpoint for comprehensive view
      const response = await tasksAPI.getAllActiveTasks();
      console.log('All tasks response:', response.data);
      
      setTasks(response.data?.today || []);
      setOverdueTasks(response.data?.overdue || []);
      setUpcomingTasks(response.data?.upcoming || []);
      
      // Calculate current load (including overdue tasks that affect today's capacity)
      const allActiveTasks = [
        ...(response.data?.today || []),
        ...(response.data?.overdue || [])
      ];
      const currentLoad = calculateCurrentLoad(allActiveTasks);
      setCapacity(prev => ({ ...prev, current: currentLoad }));
    } catch (error) {
      console.error('Tasks endpoint error:', error);
      setTasks([]);
      setOverdueTasks([]);
      setUpcomingTasks([]);
    }
  };

  const calculateCurrentLoad = (taskList) => {
    const weights = { light: 1, medium: 2, heavy: 3 };
    return taskList
      .filter(t => t.status === 'active')
      .reduce((sum, task) => sum + (weights[task.weight] || 0), 0);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    if (!newTask.title.trim()) {
      toast.warning('Please enter a task title');
      return;
    }

    setLoading(true);
    try {
      const response = await tasksAPI.createTask(newTask);
      
      // Show burnout warning if present (advisory only)
      if (response.data?.burnout_warning) {
        toast.info(response.data.burnout_warning.message, {
          autoClose: 5000,
          icon: '🛡️',
          style: { 
            background: '#1a1a1a', 
            color: '#FFB6C1',
            border: '1px solid #FFB6C1'
          }
        });
      }
      
      toast.success('Task added!', {
        icon: '✓',
        style: { 
          background: '#1a1a1a', 
          color: '#FFB6C1',
          border: '1px solid #FFB6C1'
        }
      });
      setNewTask({ 
        title: '', 
        description: '',
        weight: 'medium',
        scheduled_date: getLocalDateString()
      });
      fetchTasks();
    } catch (error) {
      // Handle different error types
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to create task';
      toast.error(errorMessage, {
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await tasksAPI.updateTask(taskId, { status: 'completed' });
      
      // Gentle success feedback
      toast.success('Done', {
        icon: '✓',
        autoClose: 2000,
        style: { 
          background: '#1a1a1a', 
          color: '#4ade80',
          border: '1px solid #4ade80'
        }
      });
      
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeferTask = async (taskId) => {
    try {
      // Move to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const year = tomorrow.getFullYear();
      const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
      const day = String(tomorrow.getDate()).padStart(2, '0');
      const tomorrowDateString = `${year}-${month}-${day}`;
      
      await tasksAPI.updateTask(taskId, { 
        status: 'deferred',
        scheduled_date: tomorrowDateString
      });
      
      toast.info('Moved to tomorrow', {
        autoClose: 2000,
        style: { 
          background: '#1a1a1a', 
          color: '#999'
        }
      });
      
      fetchTasks();
    } catch (error) {
      toast.error('Failed to defer task');
    }
  };

  const handlePauseTask = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'paused' ? 'active' : 'paused';
      await tasksAPI.updateTask(taskId, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Resumed' : 'Paused', {
        autoClose: 2000,
        style: { background: '#1a1a1a', color: '#FFB6C1' }
      });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Remove this task?')) return;
    
    try {
      await tasksAPI.deleteTask(taskId);
      toast.success('Removed', {
        autoClose: 2000,
        style: { background: '#1a1a1a', color: '#999' }
      });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Helper function to calculate days overdue
  const getDaysOverdue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to move overdue task to today
  const handleMoveToToday = async (taskId) => {
    try {
      const today = getLocalDateString();
      await tasksAPI.updateTask(taskId, { 
        scheduled_date: today
      });
      
      toast.success('Moved to today', {
        autoClose: 2000,
        style: { 
          background: '#1a1a1a', 
          color: '#FFB6C1'
        }
      });
      
      fetchTasks();
    } catch (error) {
      toast.error('Failed to move task');
    }
  };

  // Auto-watch system (runs on load calculation)
  useEffect(() => {
    if (capacity.current > capacity.max * 1.5 && tasks.length > 0) {
      toast.info("You're overloaded. Consider pausing some tasks.", {
        autoClose: 5000,
        style: { background: '#1a1a1a', color: '#FFB6C1' }
      });
    }
  }, [capacity, tasks]);

  const activeTasks = tasks.filter(t => t.status === 'active' && !t.is_company_task);
  const companyTasks = tasks.filter(t => t.status === 'active' && t.is_company_task);
  const deferredTasks = tasks.filter(t => t.status === 'deferred');
  const pausedTasks = tasks.filter(t => t.status === 'paused');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  // Calculate load percentage
  const loadPercentage = capacity.max > 0 
    ? Math.min((capacity.current / capacity.max) * 100, 100) 
    : 0;

  const getLoadColor = () => {
    if (loadPercentage >= 100) return '#FF69B4';
    if (loadPercentage >= 80) return '#FFB6C1';
    return '#FFB6C150';
  };

  const getLoadMessage = () => {
    if (loadPercentage >= 100) return "You're at capacity. Time for a pause.";
    if (loadPercentage >= 80) return "You've done enough for now.";
    if (loadPercentage >= 60) return "Good progress today.";
    return "Steady pace today.";
  };

  // Capacity Ring Component with gentle animations
  const CapacityRing = () => {
    const size = 140;
    const strokeWidth = 14;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (loadPercentage / 100) * circumference;

    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        <div style={{ position: 'relative', width: size, height: size }}>
          <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#1a1a1a"
              strokeWidth={strokeWidth}
            />
            {/* Progress circle with gentle glow */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={getLoadColor()}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ 
                transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease',
                filter: loadPercentage >= 80 ? 'drop-shadow(0 0 8px #FFB6C180)' : 'none'
              }}
            />
          </svg>
          {/* Center content */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FFB6C1' }}>
              {capacity.current}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666' }}>
              / {capacity.max}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#FFB6C1', marginTop: '0.25rem' }}>
              load points
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', maxWidth: '200px' }}>
          <div style={{ 
            fontSize: '0.85rem', 
            color: '#999',
            marginBottom: '0.25rem'
          }}>
            {getLoadMessage()}
          </div>
          {moodToday && (
            <div style={{ fontSize: '0.75rem', color: '#666' }}>
              Mood: {moodToday} • {recoveryActive ? 'Recovery Mode' : 'Normal Mode'}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Task Card Component
  const TaskCard = ({ task, type = 'active', showDueDate = false }) => {
    const weightInfo = TASK_WEIGHTS.find(w => w.value === task.weight);
    const daysOverdue = showDueDate && task.due_date ? getDaysOverdue(task.due_date) : 0;
    const isTracked = activeTrackers.some(t => t.task_id === task.id && !t.end_time);
    
    const getCardStyle = () => {
      switch (type) {
        case 'active':
          return {
            backgroundColor: '#1a1a1a',
            border: `1px solid ${weightInfo?.color}`,
            opacity: 1,
            transition: 'all 0.3s ease'
          };
        case 'overdue':
          return {
            backgroundColor: '#1a1a1a',
            border: '2px solid #ef4444',
            opacity: 1,
            transition: 'all 0.3s ease'
          };
        case 'deferred':
          return {
            backgroundColor: '#0a0a0a',
            border: '1px solid #333',
            opacity: 0.5
          };
        case 'paused':
          return {
            backgroundColor: '#0a0a0a',
            border: `1px solid ${weightInfo?.color}40`,
            opacity: 0.7
          };
        case 'completed':
          return {
            backgroundColor: '#0a0a0a',
            border: '1px solid #4ade8040',
            opacity: 0.4
          };
        default:
          return {};
      }
    };

    return (
      <div 
        style={{
          ...getCardStyle(),
          borderRadius: '12px',
          padding: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            {/* Rounded checkbox visual */}
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              border: `2px solid ${type === 'completed' ? '#4ade80' : weightInfo?.color}`,
              backgroundColor: type === 'completed' ? '#4ade8020' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {type === 'completed' && (
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.7rem', color: '#4ade80' }} />
              )}
            </div>
            <h4 style={{ 
              margin: 0,
              textDecoration: type === 'completed' ? 'line-through' : 'none',
              color: type === 'completed' ? '#666' : '#fff'
            }}>
              {task.title}
            </h4>
            <div style={{
              padding: '0.2rem 0.6rem',
              borderRadius: '8px',
              fontSize: '0.7rem',
              fontWeight: '600',
              backgroundColor: `${weightInfo?.color}20`,
              color: weightInfo?.color,
              border: `1px solid ${weightInfo?.color}40`
            }}>
              {weightInfo?.label} ({weightInfo?.points})
            </div>
            {isTracked && type === 'active' && (
              <div style={{
                padding: '0.2rem 0.6rem',
                borderRadius: '8px',
                fontSize: '0.7rem',
                fontWeight: '600',
                backgroundColor: '#FFB6C120',
                color: '#FFB6C1',
                border: '1px solid #FFB6C140',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}>
                <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.6rem' }} />
                Tracking
              </div>
            )}
            {showDueDate && daysOverdue > 0 && (
              <div style={{
                padding: '0.2rem 0.6rem',
                borderRadius: '8px',
                fontSize: '0.7rem',
                fontWeight: '600',
                backgroundColor: '#ef444420',
                color: '#ef4444',
                border: '1px solid #ef444440'
              }}>
                {daysOverdue} day{daysOverdue > 1 ? 's' : ''} overdue
              </div>
            )}
          </div>
          
          {task.description && (
            <p style={{ 
              color: type === 'completed' ? '#555' : '#ccc', 
              fontSize: '0.9rem', 
              margin: '0 0 0 2rem',
              textDecoration: type === 'completed' ? 'line-through' : 'none'
            }}>
              {task.description}
            </p>
          )}
        </div>
        
        {type !== 'completed' && (
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            {(type === 'active' || type === 'overdue') && (
              <>
                <button 
                  onClick={() => handleCompleteTask(task.id)} 
                  title="Complete"
                  style={{
                    padding: '0.6rem 0.8rem',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #4ade80',
                    borderRadius: '8px',
                    color: '#4ade80',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4ade8020';
                    e.currentTarget.style.boxShadow = '0 0 8px #4ade8040';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0a0a0a';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </button>
                {type === 'overdue' && (
                  <button 
                    onClick={() => handleMoveToToday(task.id)} 
                    title="Move to Today"
                    style={{
                      padding: '0.6rem 0.8rem',
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #FFB6C1',
                      borderRadius: '8px',
                      color: '#FFB6C1',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#FFB6C120';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#0a0a0a';
                    }}
                  >
                    📅
                  </button>
                )}
                <button 
                  onClick={() => handleDeferTask(task.id)} 
                  title="Move to Tomorrow"
                  style={{
                    padding: '0.6rem 0.8rem',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #999',
                    borderRadius: '8px',
                    color: '#999',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#99999920';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0a0a0a';
                  }}
                >
                  <FontAwesomeIcon icon={faForward} />
                </button>
                <button 
                  onClick={() => handlePauseTask(task.id, task.status)} 
                  title="Pause"
                  style={{
                    padding: '0.6rem 0.8rem',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #FFB6C1',
                    borderRadius: '8px',
                    color: '#FFB6C1',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFB6C120';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0a0a0a';
                  }}
                >
                  <FontAwesomeIcon icon={faPause} />
                </button>
              </>
            )}
            
            {type === 'paused' && (
              <button 
                onClick={() => handlePauseTask(task.id, task.status)}
                style={{
                  padding: '0.6rem 1.2rem',
                  backgroundColor: '#FFB6C1',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 0 12px #FFB6C140'
                }}
              >
                <FontAwesomeIcon icon={faPlay} /> Resume
              </button>
            )}
            
            <button 
              onClick={() => handleDeleteTask(task.id)}
              title="Delete"
              style={{
                padding: '0.6rem 0.8rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                color: '#ef4444',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#ef444420';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#0a0a0a';
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', padding: '2rem' }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '2rem',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#FFB6C120';
            e.currentTarget.style.boxShadow = '0 0 8px #FFB6C140';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
          Bestie Tasks
        </h1>
        <p style={{ color: '#999', marginBottom: '1rem' }}>Work that fits your energy</p>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '1px solid #222', paddingBottom: '0.75rem' }}>
          {[{ id: 'my-tasks', label: '📋 My Tasks' }, { id: 'reports', label: '📊 Reports' }].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.55rem 1.25rem',
                borderRadius: '8px',
                border: `1px solid ${activeTab === tab.id ? '#FFB6C1' : '#333'}`,
                backgroundColor: activeTab === tab.id ? '#FFB6C120' : 'transparent',
                color: activeTab === tab.id ? '#FFB6C1' : '#666',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? '700' : '400',
                fontSize: '0.9rem',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ===== REPORTS TAB ===== */}
        {activeTab === 'reports' && (
          <div>
            {myDepartments.length === 0 ? (
              <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '16px', padding: '3rem', textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                <h3 style={{ color: '#FFB6C1', marginBottom: '0.5rem' }}>No Department Found</h3>
                <p style={{ color: '#999', margin: 0 }}>You need to be a member of a company department to view reports.</p>
              </div>
            ) : (
              <>
                {/* Department selector */}
                <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ color: '#FFB6C1', marginBottom: '1rem', fontSize: '1.1rem' }}>Task Completion Reports</h3>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <label style={{ display: 'block', color: '#999', fontSize: '0.82rem', marginBottom: '0.4rem' }}>Department</label>
                      <select
                        value={selectedReportDept || ''}
                        onChange={e => setSelectedReportDept(e.target.value ? parseInt(e.target.value) : null)}
                        style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#0d0d0d', color: '#fff', fontSize: '0.9rem' }}
                      >
                        <option value=''>— Select department —</option>
                        {myDepartments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#999', fontSize: '0.82rem', marginBottom: '0.4rem' }}>From</label>
                      <input type='date' value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)}
                        style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#0d0d0d', color: '#fff', fontSize: '0.9rem' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#999', fontSize: '0.82rem', marginBottom: '0.4rem' }}>To</label>
                      <input type='date' value={reportDateTo} onChange={e => setReportDateTo(e.target.value)}
                        style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #444', backgroundColor: '#0d0d0d', color: '#fff', fontSize: '0.9rem' }} />
                    </div>
                    {selectedReportDept && (
                      <button
                        onClick={() => loadDeptReport(selectedReportDept)}
                        style={{ padding: '0.6rem 1.25rem', backgroundColor: '#FFB6C1', border: 'none', borderRadius: '8px', color: '#000', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        {loadingReport ? <FontAwesomeIcon icon={faSpinner} spin /> : '📊'} Generate
                      </button>
                    )}
                  </div>
                </div>

                {/* Report output */}
                {selectedReportDept && (
                  loadingReport ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                      <FontAwesomeIcon icon={faSpinner} spin size='2x' style={{ color: '#FFB6C1' }} />
                    </div>
                  ) : !deptReport ? (
                    <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '16px', padding: '3rem', textAlign: 'center', color: '#666' }}>
                      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📊</div>
                      <p>Select a date range and click Generate to view the report.</p>
                    </div>
                  ) : (
                    <>
                      {/* KPI Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                        {[
                          { label: 'Total Tasks', value: deptReport.total ?? deptTasksForReport.length, color: '#FFB6C1' },
                          { label: 'Completed', value: deptReport.completed, color: '#4ade80' },
                          { label: 'Late / Overdue', value: deptReport.late, color: '#ef4444' },
                          { label: 'Completion Rate', value: `${deptReport.completion_rate ?? 0}%`, color: '#f59e0b' },
                        ].map(({ label, value, color }) => (
                          <div key={label} style={{ backgroundColor: '#1a1a1a', border: `1px solid ${color}30`, borderRadius: '14px', padding: '1.25rem', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.78rem', color: '#999', marginBottom: '0.3rem' }}>{label}</div>
                            <div style={{ fontSize: '1.9rem', fontWeight: '800', color }}>{value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Progress bar */}
                      <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '14px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span style={{ color: '#aaa', fontSize: '0.9rem' }}>Overall Completion</span>
                          <span style={{ color: '#4ade80', fontWeight: '700' }}>{deptReport.completion_rate ?? 0}%</span>
                        </div>
                        <div style={{ height: '12px', borderRadius: '8px', backgroundColor: '#0a0a0a', border: '1px solid #333', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${deptReport.completion_rate ?? 0}%`,
                            backgroundColor: (deptReport.completion_rate ?? 0) >= 75 ? '#4ade80' : (deptReport.completion_rate ?? 0) >= 40 ? '#f59e0b' : '#ef4444',
                            borderRadius: '8px',
                            transition: 'width 0.6s ease'
                          }} />
                        </div>
                      </div>

                      {/* Per-member breakdown */}
                      {deptReport.by_assignee && deptReport.by_assignee.length > 0 && (
                        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '14px', padding: '1.25rem' }}>
                          <h3 style={{ color: '#FFB6C1', marginBottom: '1rem', fontSize: '1rem' }}>Per-Member Breakdown</h3>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid #333' }}>
                                  {['Member', 'Total', 'Completed', 'Late', 'Rate'].map(h => (
                                    <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', color: '#FFB6C1', fontSize: '0.82rem', fontWeight: '700' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {deptReport.by_assignee.map(row => {
                                  const rate = row.total ? Math.round((row.completed / row.total) * 100) : 0;
                                  return (
                                    <tr key={row.name} style={{ borderBottom: '1px solid #1e1e1e' }}>
                                      <td style={{ padding: '0.75rem', color: '#fff', fontWeight: '600', fontSize: '0.9rem' }}>{row.name}</td>
                                      <td style={{ padding: '0.75rem', color: '#aaa' }}>{row.total}</td>
                                      <td style={{ padding: '0.75rem' }}><span style={{ color: '#4ade80', fontWeight: '600' }}>{row.completed}</span></td>
                                      <td style={{ padding: '0.75rem' }}><span style={{ color: row.late > 0 ? '#ef4444' : '#666', fontWeight: row.late > 0 ? '700' : '400' }}>{row.late}</span></td>
                                      <td style={{ padding: '0.75rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <div style={{ flex: 1, height: '6px', borderRadius: '4px', backgroundColor: '#0a0a0a', border: '1px solid #333', overflow: 'hidden', minWidth: '60px' }}>
                                            <div style={{ height: '100%', width: `${rate}%`, backgroundColor: rate >= 75 ? '#4ade80' : rate >= 40 ? '#f59e0b' : '#ef4444', borderRadius: '4px' }} />
                                          </div>
                                          <span style={{ color: rate >= 75 ? '#4ade80' : rate >= 40 ? '#f59e0b' : '#ef4444', fontSize: '0.8rem', fontWeight: '700', minWidth: '34px' }}>{rate}%</span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )
                )}
              </>
            )}
          </div>
        )}

        {/* ===== MY TASKS TAB ===== */}
        {activeTab === 'my-tasks' && (
          <>

        {/* Capacity Overview */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '2rem'
        }}>
          <div style={{ flex: 1, minWidth: '280px' }}>
            <h2 style={{ fontSize: '1.4rem', marginBottom: '0.75rem', color: '#FFB6C1' }}>
              Today's Capacity
            </h2>
            <p style={{ color: '#999', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Tasks convert into load points. The system adjusts your workload based on mood, 
              recovery mode, and past completion patterns.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '1rem' }}>
              <div style={{ 
                backgroundColor: '#0a0a0a',
                padding: '0.75rem',
                borderRadius: '10px',
                border: '1px solid #333'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>Active</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#FFB6C1' }}>
                  {activeTasks.length}
                </div>
              </div>
              {overdueTasks.length > 0 && (
                <div style={{ 
                  backgroundColor: '#0a0a0a',
                  padding: '0.75rem',
                  borderRadius: '10px',
                  border: '1px solid #ef4444'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>Overdue</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
                    {overdueTasks.length}
                  </div>
                </div>
              )}
              <div style={{ 
                backgroundColor: '#0a0a0a',
                padding: '0.75rem',
                borderRadius: '10px',
                border: '1px solid #333'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>Done</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4ade80' }}>
                  {completedTasks.length}
                </div>
              </div>
              <div style={{ 
                backgroundColor: '#0a0a0a',
                padding: '0.75rem',
                borderRadius: '10px',
                border: '1px solid #333'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>Paused</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#999' }}>
                  {pausedTasks.length}
                </div>
              </div>
            </div>
          </div>
          <div>
            <CapacityRing />
          </div>
        </div>

        {/* Add Task Form */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ marginBottom: '1.5rem', color: '#FFB6C1' }}>Add Task</h3>
          <form onSubmit={handleCreateTask}>
            <div style={{ marginBottom: '1.25rem' }}>
              <input
                type="text"
                placeholder="What needs to be done?"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #333',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFB6C1'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <textarea
                placeholder="Description (optional)"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #333',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  minHeight: '80px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFB6C1'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', color: '#999', fontSize: '0.9rem' }}>
                Task Weight
              </label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                {TASK_WEIGHTS.map((weight) => (
                  <button
                    key={weight.value}
                    type="button"
                    onClick={() => setNewTask({ ...newTask, weight: weight.value })}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      backgroundColor: newTask.weight === weight.value ? `${weight.color}30` : '#0a0a0a',
                      border: `2px solid ${newTask.weight === weight.value ? weight.color : '#333'}`,
                      borderRadius: '10px',
                      color: newTask.weight === weight.value ? weight.color : '#999',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      boxShadow: newTask.weight === weight.value ? `0 0 12px ${weight.color}40` : 'none'
                    }}
                  >
                    {weight.label} ({weight.points}pt)
                  </button>
                ))}
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading || !newTask.title.trim()}
              style={{
                padding: '1rem 2rem',
                backgroundColor: newTask.title.trim() ? '#FFB6C1' : '#333',
                border: 'none',
                borderRadius: '10px',
                color: newTask.title.trim() ? '#000' : '#666',
                fontWeight: '600',
                fontSize: '1rem',
                cursor: loading || !newTask.title.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !newTask.title.trim() ? 0.7 : 1,
                transition: 'all 0.2s',
                boxShadow: newTask.title.trim() ? '0 0 16px #FFB6C140' : 'none'
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> {loading ? 'Adding...' : 'Add Task'}
            </button>
          </form>
        </div>

        {/* Overdue Tasks - Priority Display */}
        {overdueTasks.length > 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '2px solid #ef4444',
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 0 20px #ef444420'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <h2 style={{ 
                fontSize: '1.5rem', 
                color: '#ef4444',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                ⚠️ Overdue Tasks ({overdueTasks.length})
              </h2>
              <p style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
                These tasks are affecting your capacity today. Complete or reschedule them.
              </p>
            </div>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {overdueTasks.map((task) => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  type="overdue" 
                  showDueDate={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* Company-Assigned Tasks */}
        {companyTasks.length > 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '2px solid #FFB6C1',
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 0 20px #FFB6C120'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.5rem', color: '#FFB6C1', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🏢 Assigned by Manager ({companyTasks.length})
              </h2>
              <p style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
                These tasks were assigned to you by your manager or company admin.
              </p>
            </div>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {companyTasks.map((task) => (
                <div key={task.id} style={{
                  backgroundColor: '#0d0d0d',
                  border: '1px solid #FFB6C1',
                  borderRadius: '12px',
                  padding: '1.25rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                        <span style={{
                          backgroundColor: '#FFB6C120',
                          color: '#FFB6C1',
                          border: '1px solid #FFB6C140',
                          borderRadius: '6px',
                          padding: '0.1rem 0.5rem',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          letterSpacing: '0.05em'
                        }}>COMPANY TASK</span>
                        <h4 style={{ margin: 0, color: '#fff', fontSize: '1rem' }}>{task.title}</h4>
                        {task.weight && (
                          <span style={{
                            padding: '0.15rem 0.5rem', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '600',
                            backgroundColor: task.weight === 'heavy' ? '#FF69B420' : task.weight === 'medium' ? '#FFB6C120' : '#99999920',
                            color: task.weight === 'heavy' ? '#FF69B4' : task.weight === 'medium' ? '#FFB6C1' : '#999',
                            border: `1px solid ${task.weight === 'heavy' ? '#FF69B440' : task.weight === 'medium' ? '#FFB6C140' : '#99999940'}`
                          }}>{task.weight}</span>
                        )}
                      </div>
                      {task.description && <p style={{ color: '#bbb', fontSize: '0.85rem', margin: '0 0 0.4rem' }}>{task.description}</p>}
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        {task.assigned_by_name && (
                          <span style={{ color: '#888', fontSize: '0.8rem' }}>👤 Assigned by: <strong style={{ color: '#FFB6C1' }}>{task.assigned_by_name}</strong></span>
                        )}
                        {task.scheduled_date && (
                          <span style={{ color: '#888', fontSize: '0.8rem' }}>📅 Due: {task.scheduled_date}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button onClick={() => handleCompleteTask(task.id)} title="Complete" style={{
                        padding: '0.6rem 1.2rem', backgroundColor: '#0a0a0a',
                        border: '1px solid #4ade80', borderRadius: '8px', color: '#4ade80', cursor: 'pointer', transition: 'all 0.2s'
                      }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#4ade8020'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#0a0a0a'}>
                        ✓ Complete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Tasks */}
        {activeTasks.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#FFB6C1' }}>
              Active Tasks
            </h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {activeTasks.map((task) => (
                <TaskCard key={task.id} task={task} type="active" />
              ))}
            </div>
          </div>
        )}

        {/* Paused Tasks */}
        {pausedTasks.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#999' }}>
              Paused Tasks
            </h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {pausedTasks.map((task) => (
                <TaskCard key={task.id} task={task} type="paused" />
              ))}
            </div>
          </div>
        )}

        {/* Completed Tasks (Collapsible) */}
        {completedTasks.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              style={{
                background: 'none',
                border: 'none',
                color: '#4ade80',
                fontSize: '1.5rem',
                marginBottom: '1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: 0
              }}
            >
              <FontAwesomeIcon icon={showCompleted ? faEyeSlash : faEye} style={{ fontSize: '1rem' }} />
              Completed Today ({completedTasks.length})
            </button>
            {showCompleted && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {completedTasks.map((task) => (
                  <TaskCard key={task.id} task={task} type="completed" />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {tasks.length === 0 && companyTasks.length === 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '16px',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
            <h3 style={{ color: '#FFB6C1', marginBottom: '0.5rem' }}>No tasks yet</h3>
            <p style={{ color: '#999', margin: 0 }}>
              Add your first task above to get started. The system will track your capacity.
            </p>
          </div>
        )}

          </>
        )}
      </div>
    </div>
  );
};

export default Tasks;
